/* eslint-disable react-refresh/only-export-components --
   shared module mixing a hook/format helpers with presentational components,
   same trade-off the src/contexts/* files make. */
import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";

import { apiFetchJson } from "@/utils/apiFetch";
import { define } from "./glossary";

// Shared pieces for the Data Mastery analytics surfaces (Overview, Health,
// Score Explorer, Graph, Networks, Identifiers) — ported from the standalone
// Golden Explorer onto Vivo idioms. Design rule from review: constituent
// metrics are first-class (full contrast), never low-contrast caption text.

// ── data fetching ─────────────────────────────────────────────────────────────

/** GET a mastery endpoint under /data/portfolio/, keyed by path+params. */
export function useMasteryApi<T>(
  path: string,
  params?: Record<string, string | number | boolean | undefined>,
) {
  const qs = Object.entries(params ?? {})
    .filter(([, v]) => v !== undefined && v !== "")
    .map(
      ([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`,
    )
    .join("&");
  const url = `${import.meta.env.VITE_API_URL}/data/portfolio/${path}${qs ? `?${qs}` : ""}`;
  return useQuery({
    queryKey: ["mastery", path, qs],
    queryFn: () => apiFetchJson<T>(url),
    staleTime: 60_000,
  });
}

// ── formatting ────────────────────────────────────────────────────────────────

export const fmtNum = (v: number | null | undefined) =>
  v == null ? "—" : v.toLocaleString();
export const fmtPct = (v: number | null | undefined) =>
  v == null ? "—" : `${v}%`;

// green ≥90 / amber 50–89 / red <50 (fill-matrix + coverage semantics)
export const cellBg = (v: number) =>
  v === 0 ? "#f1f5f9" : v >= 90 ? "#DCFCE7" : v >= 50 ? "#FEF3C7" : "#FEE2E2";
export const barColor = (v: number) =>
  v >= 90 ? "#16A34A" : v >= 50 ? "#D97706" : "#DC2626";

export const TIER_COLOR: Record<string, string> = {
  GOLDEN: "gold",
  PROVISIONAL: "default",
};

// decision colors (score explorer / graph edge semantics)
export const DECISION_COLOR = {
  accept: "#16A34A",
  review: "#D97706",
  reject: "#DC2626",
} as const;

// ── presentational pieces ─────────────────────────────────────────────────────

/** Headline value plus its constituent metrics as first-class rows. */
export function MetricCard({
  label,
  value,
  rows,
  valueClassName,
}: {
  label: string;
  value: string;
  rows: Array<{ label: string; value: string }>;
  /** Override the headline size, e.g. for long identifiers. */
  valueClassName?: string;
}) {
  return (
    <div className="rounded-lg border border-[#E6EEF2] bg-white p-4 shadow-sm">
      <p className="text-sm font-medium text-[#556C75]">{label}</p>
      {/* headline numerals use the brand display face (McQueen Grotesk) */}
      <p
        className={`font-mcqueenGrotesk mt-1 font-bold text-[#2B4752] ${valueClassName ?? "text-3xl"}`}
      >
        {value}
      </p>
      <div className="mt-3 border-t border-[#E6EEF2] pt-2">
        {rows.map((r) => (
          <div
            key={r.label}
            className="flex items-baseline justify-between py-0.5"
          >
            <span className="text-sm text-[#2B4752]">{r.label}</span>
            <span className="text-sm font-semibold tabular-nums text-[#2B4752]">
              {r.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function SectionCard({
  title,
  meta,
  glossaryTerm,
  extra,
  children,
}: {
  title: string;
  meta?: string;
  /** Glossary key whose definition is appended to the tooltip. */
  glossaryTerm?: string;
  extra?: React.ReactNode;
  children: React.ReactNode;
}) {
  const definition = glossaryTerm ? define(glossaryTerm) : undefined;
  const tip = [meta, definition].filter(Boolean).join(" — ");
  return (
    <div className="mb-4 rounded-lg border border-[#E6EEF2] bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <span className="font-mcqueenGrotesk text-sm font-bold text-[#2B4752]">
            {title}
          </span>
          {/* explanatory copy lives in a tooltip, matching PageHeader */}
          {tip && (
            <Tooltip title={tip}>
              <InfoCircleOutlined className="ml-2 text-xs text-[#556C75]" />
            </Tooltip>
          )}
        </div>
        {extra}
      </div>
      {children}
    </div>
  );
}

export function HBar({
  label,
  n,
  max,
  color,
  fmtValue,
}: {
  label: string;
  n: number;
  max: number;
  color: string;
  fmtValue?: (n: number) => string;
}) {
  return (
    <div className="my-1.5 grid grid-cols-[90px_1fr_90px] items-center gap-2.5">
      <span className="text-right text-xs text-[#556C75]">{label}</span>
      <div className="h-4 rounded bg-[#f1f5f9]">
        <div
          className="h-full rounded"
          style={{ width: `${(n / max) * 100}%`, background: color }}
        />
      </div>
      <span className="text-right text-xs tabular-nums text-[#2B4752]">
        {fmtValue ? fmtValue(n) : fmtNum(n)}
      </span>
    </div>
  );
}

/** Small inline chip for gap flags / aliases / identifiers. */
export function Chip({ children }: { children: React.ReactNode }) {
  return (
    <span className="rounded bg-[#f1f5f9] px-1.5 py-0.5 text-[10.5px] text-[#556C75]">
      {children}
    </span>
  );
}

/**
 * Renders a mastering term with its glossary definition on hover (dotted
 * underline when a definition exists, plain text otherwise). Use for raw
 * vocabulary the UI surfaces — basket codes, match statuses, relationship
 * types, signal names.
 */
export function Term({
  term,
  children,
}: {
  term: string;
  children?: React.ReactNode;
}) {
  const definition = define(term);
  const body = children ?? term;
  if (!definition) return <>{body}</>;
  return (
    <Tooltip title={definition}>
      <span className="cursor-help underline decoration-dotted underline-offset-2">
        {body}
      </span>
    </Tooltip>
  );
}
